import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { FlexGap, Input, Tags } from '@treams/treams-ui';

export function DateTimeInput(props: {
	value: Date;
	onChange: (value: Date) => void;
	disabled?: boolean;
}) {
	const { t } = useTranslation();
	const precision = 'minutes';

	const getMomentTime = useCallback(
		(value: moment.MomentInput) => {
			return moment(value).diff(moment(value).startOf('day'), precision);
		},
		[precision],
	);

	const setMomentTime = useCallback(
		(date: moment.Moment, time: number) => {
			return date.add(time, precision).toDate();
		},
		[precision],
	);

	const onChangeDate = (value: Date) => {
		const newDate = moment(value).startOf('day');
		const currentTime = getMomentTime(props.value);

		props.onChange(setMomentTime(newDate, currentTime));
	};

	const [time, setTime] = useState(() => formatTime(props.value));
	const [isTimeValid, setIsTimeValid] = useState(true);

	const onChangeTime = (value: string) => {
		setTime(value);

		const timeOnly = moment(value, 'HH:mm');
		const isValid = timeOnly.isValid();
		setIsTimeValid(isValid);

		if (isValid) {
			const currentDate = moment(props.value).startOf('day');
			const newTime = getMomentTime(moment(value, 'HH:mm'));

			props.onChange(setMomentTime(currentDate, newTime));
		}
	};

	useEffect(() => setTime(formatTime(props.value)), [props.value]);

	return (
		<FlexGap gap={8} flexDirection="column">
			<DateTimePickerWrapper>
				<Tags.Item.DatePicker
					label={t('common.date')}
					value={props.value}
					onChange={onChangeDate}
					flexGrow={1}
					disabled={props.disabled}
				/>
				<Input
					type="time"
					inputProps={{ step: 5 * 60 }}
					value={time}
					onChange={onChangeTime}
					onBlur={() => setTime(formatTime(props.value))}
					error={isTimeValid ? undefined : t('common.time.invalid')}
					disabled={props.disabled}
				/>
			</DateTimePickerWrapper>
		</FlexGap>
	);
}

const DateTimePickerWrapper = styled(FlexGap).attrs({
	gap: 8,
	alignItems: 'center',
})`
	--size: 40px;

	> div:first-child {
		flex-grow: 1;
		width: 100%;
		height: var(--size);

		> * {
			height: 100%;
			line-height: var(--size);
			border-radius: 6px; // Matches input border radius.
		}
	}

	input[type='time'] {
		// Fixes height alignment as the input uses a box shadow to emulate a border.
		height: calc(var(--size) - 1px);
	}
`;

const formatTime = (value: Date) => moment(value).format('HH:mm');
